import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Input, Space, Row, Col, DatePicker, Drawer, Table, Tag, Alert, Collapse, Modal } from 'antd';
import { ClockCircleOutlined, SaveOutlined, UserOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import BaseSelect from '@/components/Elements/BaseSelect';
import { _newLine, appointmentName, isValidDateForm, renderAppointmentStatus, isValidDate, renderAppointmentName, serializeDateTime } from '@/utils/helpers';
import { createAppointment, updateAppointment } from '@/redux/actions/appointmentActions';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { getCustomerServiceDoctorRoleGroups } from '@/components/customer-services/customerServiceDoctorRoleHelpers';

const { Panel } = Collapse;

function AppointmentForm({
    visible,
    currentItem,
    customerId,
    onCancel,
    reloadData,
}) {
    const dispatch = useDispatch();

    const doctors = useSelector((state) => state.config.doctors);
    const assistants = useSelector((state) => state.config.assistants);
    const filterReducer = useSelector((state) => state.filterReducer.list);

    const branchActiveList = filterReducer?.branches || [];

    const [form] = Form.useForm();

    const watchedServiceItems = Form.useWatch('service_items', form);

    const [isLoading, setIsLoading] = useState(false);
    const [requireTime, setRequireTime] = useState(true);
    const [showDoctor, setShowDoctor] = useState(false);
    const [switchStatus, setSwitchStatus] = useState(false);

    const [activeServiceCollapseKeys, setActiveServiceCollapseKeys] = useState([]);

    // NEW: customerData + loadingCustomer
    const [customerData, setCustomerData] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(false);

    const isUpdate = !!currentItem?.id;

    const resolvedCustomerId = currentItem?.customer_id || customerId || null;

    // NEW: tự fetch customer khi không có currentItem.customer và có customerId
    useEffect(() => {
        if (!visible || !resolvedCustomerId) {
            setCustomerData(null);
            return;
        }

        let cancelled = false;

        setLoadingCustomer(true);

        dispatch(fetchCustomer(resolvedCustomerId))
            .then((res) => {
                if (!cancelled) {
                    setCustomerData(res);
                }
            })
            .catch((error) => {
                console.error('Lỗi khi tải thông tin khách hàng:', error);
                if (!cancelled) {
                    setCustomerData(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingCustomer(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [visible, resolvedCustomerId, dispatch]);

    // UPDATE: serviceOptions lấy từ customerData thay vì currentItem.customer
    const serviceOptions = useMemo(() => {
        return (customerData?.active_purchased_services || []).map((item) => {
            const roleGroups = getCustomerServiceDoctorRoleGroups(item);
            const idsForRole = roleKey => roleGroups.find(group => group.key === roleKey)?.people
                .map(person => Number(person.id)).filter(Boolean) || [];
            const examinationDoctorIds = idsForRole('examination');
            const treatmentDoctorIds = idsForRole('treatment');
            const implantDoctorIds = idsForRole('implant_placement');
            const prostheticDoctorIds = idsForRole('prosthetic_restoration');

            return {
                id: item.id,
                name: item.service?.name || '',
                code: item.service?.code || '',
                examination_doctor_ids: examinationDoctorIds,
                treatment_doctor_ids: treatmentDoctorIds,
                implant_doctor_ids: implantDoctorIds,
                prosthetic_doctor_ids: prostheticDoctorIds,
                is_implant: roleGroups.some(group => ['implant_placement', 'prosthetic_restoration'].includes(group.key))
                    || item?.service?.service_group?.code === 'implant',
                assistant_ids: (item.assistants || []).map((a) => Number(a.assistant_id)).filter(Boolean),
            };
        });
    }, [customerData]);

    const columns = useMemo(
        () => [
            {
                title: 'Kiểu lịch hẹn',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) => <strong>{renderAppointmentName(record.name)}</strong>,
            },
            {
                title: 'Thời gian',
                dataIndex: 'scheduled_at',
                key: 'tscheduled_ate',
                width: '140px',
                render: (text) => <div>{isValidDate(text)}</div>,
            },
            {
                title: '#',
                width: '100px',
                render: (text, record) => {
                    const diffDate = record.scheduled_at
                        ? moment(record.scheduled_at, 'YYYY-MM-DD').diff(moment().startOf('day'), 'days')
                        : 0;

                    return (
                        <div>
                            {diffDate ? (
                                <span>
                                    {diffDate > 0 ? (
                                        <Tag color="#f50">Còn lại {diffDate} ngày</Tag>
                                    ) : (
                                        <Tag color="red">Cách đây {-1 * diffDate} ngày</Tag>
                                    )}
                                </span>
                            ) : (
                                <>{record.scheduled_at != null ? <Tag color="#096dd9">Hôm nay</Tag> : null}</>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Trạng thái',
                dataIndex: 'status',
                key: 'status',
                width: '100px',
                render: (text) => <div>{renderAppointmentStatus(text)}</div>,
            },
        ],
        []
    );

    const computeFlagsFromName = useCallback((name, status) => {
        const _requireTime = !(name === 'CHUADATDUOCLICH' || name === 'CHUAXACDINHLICH' || name === 'KHAC');
        const _showDoctor = name === 'LAMDICHVU' || name === 'TAIKHAM';
        const _switchStatus = name === 'GOILAI';

        setRequireTime(_requireTime);
        setShowDoctor(_showDoctor);
        setSwitchStatus(_switchStatus);
    }, []);

    const buildServiceItemsFromSelectedIds = useCallback(
        (selectedIds = [], prevItems = []) => {
            const prevMap = new Map((prevItems || []).map((it) => [String(it.customer_service_id), it]));

            return (selectedIds || []).map((id) => {
                const key = String(id);
                const sv = serviceOptions.find((x) => String(x.id) === key);
                const prev = prevMap.get(key);

                const assistant_ids = (prev?.assistant_ids ?? sv?.assistant_ids ?? sv?.assistants?.map((a) => a.user_id) ?? [])
                    .map(Number)
                    .filter(Boolean);

                return {
                    customer_service_id: id,
                    examination_doctor_ids: (prev?.examination_doctor_ids ?? sv?.examination_doctor_ids ?? []).map(Number).filter(Boolean),
                    treatment_doctor_ids: (prev?.treatment_doctor_ids ?? sv?.treatment_doctor_ids ?? []).map(Number).filter(Boolean),
                    implant_doctor_ids: (prev?.implant_doctor_ids ?? sv?.implant_doctor_ids ?? []).map(Number).filter(Boolean),
                    prosthetic_doctor_ids: (prev?.prosthetic_doctor_ids ?? sv?.prosthetic_doctor_ids ?? []).map(Number).filter(Boolean),
                    assistant_ids,
                };
            });
        },
        [serviceOptions]
    );

    const onChangeServices = useCallback(
        (selectedIds) => {
            const prevItems = form.getFieldValue('service_items') || [];
            const nextItems = buildServiceItemsFromSelectedIds(selectedIds, prevItems);
            form.setFieldsValue({ service_items: nextItems });

            // tự động mở các panel tương ứng với dịch vụ đang chọn
            setActiveServiceCollapseKeys((selectedIds || []).map((id) => String(id)));
        },
        [form, buildServiceItemsFromSelectedIds]
    );

    const onChangeName = useCallback(
        (value) => {
            computeFlagsFromName(value, form.getFieldValue('status'));

            // set status mặc định theo loại lịch
            const nextStatus = value === 'GOILAI' ? 'chuagoi' : 'chuaden';
            form.setFieldsValue({ status: nextStatus });

            // nếu không cần giờ -> reset scheduled_at
            if (value === 'CHUADATDUOCLICH' || value === 'CHUAXACDINHLICH' || value === 'KHAC') {
                form.setFieldsValue({ scheduled_at: null });
            }
        },
        [computeFlagsFromName, form]
    );

    const onClose = useCallback(() => {
        form.resetFields();
        onCancel();
        setIsLoading(false);
        setRequireTime(true);
        setShowDoctor(false);
        setSwitchStatus(false);
        setActiveServiceCollapseKeys([]);
        // không reset customerData để tránh flash khi mở lại nhanh; nhưng bạn có thể reset nếu muốn
    }, [form, onCancel]);

    const showDuplicateConfirm = (payload) => {
        const { existing_schedules, message } = payload;

        Modal.confirm({
            title: 'Lịch hẹn bị trùng cơ sở',
            okText: 'Vẫn cập nhật lịch',
            cancelText: 'Huỷ',
            bodyStyle: { padding: 15 },
            className: 'confirm-modal',
            content: (
                <>
                    <Alert
                        style={{ fontSize: '1.1em' }}
                        type="warning"
                        message={message}
                        className="mb-3 alert-text-red"
                    />
                    <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={existing_schedules}
                        columns={[
                            {
                                title: 'Thời gian',
                                dataIndex: 'scheduled_at',
                                render: (t) => moment(t).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                title: 'Trạng thái',
                                dataIndex: 'status',
                                render: (text, record) => (
                                    <Space size="small">
                                        {renderAppointmentName(record.name)}
                                        {renderAppointmentStatus(record.status)}
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </>
            ),
            onOk: async () => {
                await onSubmit(true);
            },
        });
    }

    // UPDATE: init form add/update; dùng customerData/customerId
    useEffect(() => {
        if (!visible) return;

        // ===== UPDATE =====
        if (isUpdate) {
            const initName = currentItem?.name || null;
            const initStatus = currentItem?.status || null;
            computeFlagsFromName(initName, initStatus);

            const apiServices = currentItem?.services || [];

            const initServiceIds =
                apiServices.length > 0
                    ? apiServices.map((s) => s.customer_service_id).filter(Boolean)
                    : currentItem?.customer_service_ids || (currentItem?.customer_service_id ? [currentItem.customer_service_id] : []);

            const initServiceItems =
                apiServices.length > 0
                    ? apiServices
                        .filter((s) => s?.customer_service_id)
                        .map((s) => {
                            const roleIds = (flag, legacyFallback = false) => (s.doctors || [])
                                .filter(d => Number(d?.pivot?.[flag] ?? d?.[flag] ?? (legacyFallback ? 1 : 0)) === 1)
                                .map(d => Number(d.id ?? d.doctor_id)).filter(Boolean);

                            return {
                                customer_service_id: s.customer_service_id,
                                examination_doctor_ids: roleIds('is_examination_doctor'),
                                treatment_doctor_ids: roleIds('is_treatment_doctor', true),
                                implant_doctor_ids: roleIds('is_implant_doctor'),
                                prosthetic_doctor_ids: roleIds('is_prosthetic'),
                                assistant_ids: (s.assistants || [])
                                    .map((a) => Number(a.id ?? a.assistant_id))
                                    .filter(Boolean),
                            };
                        })
                    : currentItem?.service_items || buildServiceItemsFromSelectedIds(initServiceIds, []);

            form.setFieldsValue({
                customer_id: currentItem?.customer_id ?? customerData?.id ?? customerId ?? null,
                name: initName,
                status: initStatus || null,
                scheduled_at: currentItem?.scheduled_at ? isValidDateForm(currentItem.scheduled_at) : null,
                branch_id: currentItem?.branch_id || null,
                note: currentItem?.note ? _newLine(currentItem.note) : '',
                customer_service_ids: initServiceIds,
                service_items: initServiceItems,
            });
            setActiveServiceCollapseKeys((initServiceIds || []).map((id) => String(id)));
            return;
        }

        // ===== ADD =====
        computeFlagsFromName(null, null);

        form.setFieldsValue({
            customer_id: customerData?.id ?? customerId ?? null,
            name: null,
            status: null,
            scheduled_at: null,
            branch_id: customerData?.branch_id ?? null,
            note: '',
            customer_service_ids: [],
            service_items: [],
        });
        setActiveServiceCollapseKeys([]);
    }, [
        visible,
        isUpdate,
        currentItem,
        customerData,
        customerId,
        form,
        computeFlagsFromName,
        buildServiceItemsFromSelectedIds,
    ]);

    // FIX 2: customerData load xong sau đó auto fill branch_id (KHÔNG ghi đè nếu user đã chọn)
    useEffect(() => {
        if (!visible) return;
        if (isUpdate) return;

        const customerBranchId = customerData?.branch_id;
        if (!customerBranchId) return;

        const currentBranchId = form.getFieldValue('branch_id');
        if (currentBranchId) return; // user đã chọn rồi thì không đụng

        // đảm bảo branchId tồn tại trong options (tránh set giá trị không có trong list)
        const existsInOptions = (branchActiveList || []).some((b) => String(b.id) === String(customerBranchId));
        if (!existsInOptions) return;

        form.setFieldsValue({ branch_id: customerBranchId });
    }, [visible, isUpdate, customerData?.branch_id, branchActiveList, form]);

    const onSubmit = useCallback(
        async (forceCreate = false) => {
            try {
                const values = await form.validateFields().catch((err) => {
                    if (err?.errorFields?.length) {
                        Modal.warning({
                            title: 'Thiếu dữ liệu',
                            content: 'Chưa chọn hết các trường bắt buộc.',
                        });
                    }
                    throw err;
                });

                setIsLoading(true);

                // scheduled_at có thể null khi requireTime=false
                if (values?.scheduled_at) values.scheduled_at = serializeDateTime(values.scheduled_at, 'datetime');

                const payload = { ...values };
                if (forceCreate) payload.force_create = true;

                const rawServiceItems = (payload.service_items || [])
                    .filter((it) => it?.customer_service_id);

                const normalizedServiceItems = rawServiceItems.map((it) => {
                    return {
                        customer_service_id: it.customer_service_id,
                        examination_doctor_ids: (it.examination_doctor_ids || []).map(Number).filter(Boolean),
                        treatment_doctor_ids: (it.treatment_doctor_ids || []).map(Number).filter(Boolean),
                        implant_doctor_ids: (it.implant_doctor_ids || []).map(Number).filter(Boolean),
                        prosthetic_doctor_ids: (it.prosthetic_doctor_ids || []).map(Number).filter(Boolean),
                        assistant_ids: (it.assistant_ids || []).map(Number).filter(Boolean),
                    };
                });

                payload.service_items = normalizedServiceItems;

                // create: bắt buộc có customer_id
                if (!isUpdate) {
                    payload.customer_id = payload.customer_id || customerData?.id || customerId;
                    payload.status = switchStatus ? 'chuagoi' : 'chuaden';

                    if (!payload.customer_id) {
                        setIsLoading(false);
                        Modal.error({
                            title: 'Thiếu khách hàng',
                            content: 'Không xác định được khách hàng để tạo lịch hẹn.',
                        });
                        return;
                    }
                }

                delete payload.customer_service_ids;

                if (isUpdate) {
                    await dispatch(updateAppointment(currentItem.id, payload));
                } else {
                    await dispatch(createAppointment(payload));
                }

                onClose();
                reloadData();
            } catch (error) {
                setIsLoading(false);

                const res = error?.response;
                const data = res?.data;
                const apiPayload = data?.data;

                if (res?.status === 409 && apiPayload?.need_confirm) {
                    showDuplicateConfirm(apiPayload);
                    return;
                }
            }
        },
        [dispatch, form,
            isUpdate, currentItem,
            customerData, customerId,
            switchStatus, onClose, reloadData,
            showDuplicateConfirm]
    );

    return (
        <Drawer
            visible={visible}
            open={visible}
            title={isUpdate ? 'Sửa lịch hẹn' : 'Tạo lịch hẹn'}
            onClose={onClose}
            maskClosable={false}
            width={isMobile ? '100%' : '60%'}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button size="large" onClick={onClose}>
                            Huỷ
                        </Button>
                        <Button disabled={isLoading} loading={isLoading} type="primary" size="large" icon={<SaveOutlined />} onClick={() => onSubmit(false)}>
                            Lưu
                        </Button>
                    </Space>
                </div>
            }
        >
            {visible ? (
                <Form form={form} layout="vertical">
                    <Form.Item name="customer_id" hidden>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24} className="mb-3">
                            <h6>
                                <UserOutlined /> Khách hàng:&nbsp;
                                <span className="color-primary">
                                    {loadingCustomer
                                        ? 'Đang tải thông tin khách hàng...'
                                        : customerData
                                            ? `${customerData.full_name} - ${customerData.code}`
                                            : (currentItem?.customer?.full_name ? `${currentItem.customer.full_name} - ${currentItem.customer.code}` : 'Chưa có thông tin khách hàng')}
                                </span>
                            </h6>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} className="mb-3">
                            <Alert
                                message="Chú ý chọn đúng kiểu lịch hẹn"
                                description={
                                    <>
                                        <p>
                                            - Lịch <strong>Điều trị:</strong> Khách vẫn đang trong quá trình thực hiện, chưa hoàn thành dịch vụ
                                            tại nha khoa.
                                        </p>
                                        <p>
                                            - Lịch <strong>Tái khám:</strong> Khách đã hoàn thành quá trình làm dịch vụ, hẹn tới thăm khám lại,
                                            kiểm tra định kỳ
                                        </p>
                                        <p>
                                            - Khi khách có nhu cầu thực hiện dịch vụ tại <strong>một cơ sở khác</strong> (khác với nơi đã đăng
                                            ký hồ sơ ban đầu), nhân viên cần chọn chính xác <strong>Cơ sở thực hiện dịch vụ</strong> tương ứng để
                                            hệ thống ghi nhận và phân phối lịch đúng nơi.
                                        </p>
                                    </>
                                }
                                type="warning"
                            />
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Collapse bordered={false} defaultActiveKey={[]} className="mb-3" expandIconPosition="end">
                                <Panel
                                    header={
                                        <span style={{ color: 'blue', fontWeight: 500 }}>
                                            <ClockCircleOutlined /> Xem lịch sử cuộc hẹn
                                        </span>
                                    }
                                    key="1"
                                >
                                    <Table
                                        size="small"
                                        rowKey="key"
                                        tableLayout="auto"
                                        columns={columns}
                                        dataSource={customerData?.appointments || currentItem?.customer?.appointments || []}
                                        pagination={false}
                                        scroll={isMobile ? { x: 'max-content' } : undefined}
                                        className="mb-4 w-100"
                                        locale={{ emptyText: 'Chưa có dữ liệu' }}
                                    />
                                </Panel>
                            </Collapse>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Kiểu lịch hẹn" name="name" rules={[{ required: true, message: 'Vui lòng chọn!' }]}>
                                <BaseSelect
                                    options={appointmentName}
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                    onChange={onChangeName}
                                />
                            </Form.Item>
                        </Col>

                        {requireTime ? (
                            <Col xs={24} md={12}>
                                <Form.Item label="Giờ hẹn" name="scheduled_at" rules={[{ required: requireTime, message: 'Vui lòng điền!' }]}>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        showTime={{ format: 'HH:mm', defaultValue: moment('00:00', 'HH:mm') }}
                                        format="DD/MM/YYYY HH:mm"
                                    />
                                </Form.Item>
                            </Col>
                        ) : null}

                        {showDoctor ? (
                            <>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Dịch vụ"
                                        name="customer_service_ids"
                                        rules={[{ required: true, message: 'Vui lòng chọn!' }]}
                                        initialValue={null}
                                    >
                                        <BaseSelect
                                            options={serviceOptions}
                                            optionValue="id"
                                            optionLabel="name"
                                            placeholder={loadingCustomer ? 'Đang tải dịch vụ...' : 'Chọn 1 hoặc nhiều dịch vụ'}
                                            showSearch
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            onChange={onChangeServices}
                                            disabled={loadingCustomer || !customerData}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.List name="service_items" initialValue={[]}>
                                        {(fields) => {
                                            const selectedIds = form.getFieldValue('customer_service_ids') || [];
                                            const totalSelected = selectedIds.length;

                                            return (
                                                <>
                                                    {totalSelected > 0 && (
                                                        <div
                                                            style={{
                                                                marginBottom: 12,
                                                                padding: '12px 16px',
                                                                background: '#f0f5ff',
                                                                border: '1px solid #d6e4ff',
                                                                borderRadius: 8,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontWeight: 600,
                                                                    color: '#1677ff',
                                                                    marginBottom: 4,
                                                                }}
                                                            >
                                                                {totalSelected} dịch vụ đang được chọn
                                                            </div>

                                                            <div
                                                                style={{
                                                                    fontSize: 14,
                                                                    color: '#595959',
                                                                }}
                                                            >
                                                                Thiết lập bác sĩ và phụ tá cho từng dịch vụ bên dưới.
                                                            </div>
                                                        </div>
                                                    )}

                                                    <Collapse
                                                        bordered={false}
                                                        activeKey={activeServiceCollapseKeys}
                                                        onChange={(keys) => setActiveServiceCollapseKeys(Array.isArray(keys) ? keys : [keys])}
                                                        className="mb-3 appointment-service-collapse"
                                                        expandIconPosition="end"
                                                    >
                                                        {fields.map((field, idx) => {
                                                            //const items = form.getFieldValue('service_items') || [];
                                                            const items = watchedServiceItems || [];
                                                            const csId = items?.[idx]?.customer_service_id;

                                                            const service = serviceOptions.find((s) => String(s.id) === String(csId));
                                                            const serviceFromApi = currentItem?.services?.find((x) => String(x.customer_service_id) === String(csId));

                                                            const serviceName =
                                                                service?.name ||
                                                                serviceFromApi?.customer_service?.service?.name ||
                                                                `Dịch vụ #${idx + 1}`;

                                                            const serviceCode =
                                                                service?.code ||
                                                                serviceFromApi?.customer_service?.service?.code ||
                                                                null;

                                                            const headerNode = (
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <span
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: 30,
                                                                            height: 30,
                                                                            borderRadius: '50%',
                                                                            background: '#e6f4ff',
                                                                            color: '#1677ff',
                                                                            fontWeight: 700,
                                                                            fontSize: 13,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {idx + 1}
                                                                    </span>

                                                                    <span style={{ fontWeight: 700 }}>{serviceName}</span>

                                                                    {serviceCode ? (
                                                                        <Tag color="blue" style={{ marginLeft: 6 }}>
                                                                            {serviceCode}
                                                                        </Tag>
                                                                    ) : null}
                                                                </span>
                                                            );

                                                            return (
                                                                <Panel
                                                                    header={headerNode}
                                                                    key={String(csId || field.key)}
                                                                    className='appointment-service-panel'
                                                                >
                                                                    <Row gutter={12}>
                                                                        {[
                                                                            ['examination_doctor_ids', 'Bác sĩ khám'],
                                                                            ['treatment_doctor_ids', 'Bác sĩ điều trị'],
                                                                            ...(service?.is_implant ? [
                                                                                ['implant_doctor_ids', 'Bác sĩ cắm trụ'],
                                                                                ['prosthetic_doctor_ids', 'Bác sĩ phục hình'],
                                                                            ] : []),
                                                                        ].map(([name, label]) => (
                                                                            <Col xs={24} md={12} key={name}>
                                                                                <Form.Item label={label} name={[field.name, name]} initialValue={[]}>
                                                                                    <BaseSelect
                                                                                        options={doctors ?? []}
                                                                                        optionValue="user_id"
                                                                                        optionLabel="display_name"
                                                                                        placeholder={`Chọn 1 hoặc nhiều ${label.toLowerCase()}`}
                                                                                        showSearch
                                                                                        mode="multiple"
                                                                                        allowClear
                                                                                        style={{ width: '100%' }}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                        ))}

                                                                        <Col xs={24} md={24}>
                                                                            <Form.Item label="Phụ tá" name={[field.name, 'assistant_ids']} initialValue={[]}>
                                                                                <BaseSelect
                                                                                    options={assistants ?? []}
                                                                                    optionValue="user_id"
                                                                                    optionLabel="display_name"
                                                                                    placeholder="Chọn 1 hoặc nhiều phụ tá"
                                                                                    showSearch
                                                                                    mode="multiple"
                                                                                    style={{ width: '100%' }}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>

                                                                        <Form.Item name={[field.name, 'customer_service_id']} hidden>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Row>
                                                                </Panel>
                                                            );
                                                        })}
                                                    </Collapse>
                                                </>
                                            );
                                        }}
                                    </Form.List>
                                </Col>
                            </>
                        ) : null}

                        <Col xs={24}>
                            <Form.Item label="Cơ sở thực hiện dịch vụ" name="branch_id" rules={[{ required: true, message: 'Vui lòng chọn' }]}>
                                <BaseSelect
                                    options={branchActiveList || []}
                                    optionValue="id"
                                    optionLabel="name"
                                    placeholder="Chọn cơ sở"
                                    className="w-100"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item label="Ghi chú" name="note">
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            ) : null}
        </Drawer>
    );
}

export default AppointmentForm;
